import { create } from 'zustand';
import { api } from '../services/api';
import { useUiStore } from './uiStore';
import type { Word, StageId, PipelineStage } from '../types';

// Pipeline stages in order
// stage7 (Social) and stage9 (Related Social) retired 2026-07-05 — social
// content moves to the future Social Engine. Pipeline: ...stage6 → stage8 →
// final review. Original stage numbering kept for the remaining stages.
export const PIPELINE_STAGES: PipelineStage[] = [
  { id: 'new', label: 'New', color: 'var(--stage-new)' },
  { id: 'stage1', label: 'Stage 1: Definition', color: 'var(--stage-generating)' },
  { id: 'stage1b', label: 'Stage 1b: Fakes', color: 'var(--stage-generating)' },
  { id: 'stage2', label: 'Stage 2: Examples', color: 'var(--stage-generating)' },
  { id: 'stage3', label: 'Stage 3: How To Use', color: 'var(--stage-generating)' },
  { id: 'stage4', label: 'Stage 4: Fun Facts', color: 'var(--stage-generating)' },
  { id: 'stage5', label: 'Stage 5: Audio', color: 'var(--stage-generating)' },
  { id: 'stage6', label: 'Stage 6: Reframes', color: 'var(--stage-generating)' },
  { id: 'stage8', label: 'Stage 8: Related Words', color: 'var(--stage-generating)' },
  { id: 'stage10', label: 'Stage 10: Final Review', color: 'var(--stage-approved)' },
  { id: 'stage11', label: 'Stage 11: Transfer', color: 'var(--stage-scheduled)' },
  { id: 'live', label: 'Live', color: 'var(--stage-live)' },
];

export const STAGE_MAP: Record<string, PipelineStage> = Object.fromEntries(PIPELINE_STAGES.map((s) => [s.id, s]));

// Determine pipeline stage from word fields
export function getWordStage(word: Record<string, unknown>): StageId {
  // Post-pipeline states
  if (word.transferred_to_d1) return 'live';
  if (word.transferred || word.send_to_games_core) return 'stage11';

  // Check stages in reverse order to find highest completed.
  // Social stages (7/9) retired: related-words approval now advances straight
  // to Final Review; reframes approval advances straight to Related Words.
  if (word.related_words_approved) return 'stage10';
  if (word.selected_third_chance_reframe) return 'stage8';
  if (word.selected_second_chance_reframe) return 'stage6';
  const funFactsReviewed = !!word.selected_fun_fact_1 || word.facts_found === 0;
  if (word.audio_url && funFactsReviewed) return 'stage5';
  if (funFactsReviewed) return 'stage4';
  if (word.selected_how_to_use_1) return 'stage3';
  if (word.selected_example_1) return 'stage2';
  if (word.selected_wrong_1) return 'stage1b';
  if (word.definition_enhanced) return 'stage1';
  return 'new';
}

export type UsageFilter = 'all' | 'empty' | 'used' | 'unused';

interface WordState {
  words: Word[];
  loading: boolean;
  error: string | null;
  selectedWordId: string | null;
  selectedWord: Word | null;
  dirtyFields: Set<string>;
  wordVersion: string | null;
  searchQuery: string;
  statusFilter: string[];
  difficultyFilter: string[];
  themeFilter: string[];
  dateFromFilter: string;
  dateToFilter: string;
  usageFilter: UsageFilter;
  setSearchQuery: (query: string) => void;
  setStatusFilter: (statuses: string[]) => void;
  setDifficultyFilter: (difficulties: string[]) => void;
  setThemeFilter: (themes: string[]) => void;
  setDateFromFilter: (date: string) => void;
  setDateToFilter: (date: string) => void;
  setUsageFilter: (filter: UsageFilter) => void;
  fetchWords: () => Promise<void>;
  selectWord: (wordId: string | null) => Promise<void>;
  setWordVersion: (version: string | null) => void;
  updateWordField: (field: string, value: unknown) => void;
  setSelectedWordField: (field: string, value: unknown) => void;
  clearDirty: () => void;
  clearDirtyFields: (fields: string[]) => void;
  removeWord: (wordId: string) => void;
  getFilteredWords: () => Word[];
  getGroupedWords: () => Record<string, Word[]>;
  getAllThemes: () => string[];
}

const FILTER_STORAGE_KEY = 'waw-binder-filters';

export function parseThemes(value: unknown): string[] {
  if (!value) return [];
  if (Array.isArray(value)) return value.map((t) => String(t).trim()).filter(Boolean);
  return String(value).split(',').map((t) => t.trim()).filter(Boolean);
}

interface PersistedFilters {
  searchQuery: string;
  statusFilter: string[];
  difficultyFilter: string[];
  themeFilter: string[];
  dateFromFilter: string;
  dateToFilter: string;
  usageFilter: UsageFilter;
}

function loadPersistedFilters(): PersistedFilters {
  const defaults: PersistedFilters = {
    searchQuery: '',
    statusFilter: [],
    difficultyFilter: [],
    themeFilter: [],
    dateFromFilter: '',
    dateToFilter: '',
    usageFilter: 'all',
  };
  if (typeof window === 'undefined') return defaults;
  try {
    const raw = sessionStorage.getItem(FILTER_STORAGE_KEY);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw);
    return { ...defaults, ...parsed };
  } catch {
    return defaults;
  }
}

function persistFilters(filters: Partial<PersistedFilters>) {
  if (typeof window === 'undefined') return;
  try {
    const current = loadPersistedFilters();
    sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify({ ...current, ...filters }));
  } catch {
    // Ignore storage errors (quota, disabled, etc.)
  }
}

const persisted = loadPersistedFilters();

export const useWordStore = create<WordState>()((set, get) => ({
  words: [],
  loading: false,
  error: null,
  selectedWordId: null,
  selectedWord: null,
  dirtyFields: new Set(),
  wordVersion: null,

  // Filters (hydrated from sessionStorage)
  searchQuery: persisted.searchQuery,
  statusFilter: persisted.statusFilter,
  difficultyFilter: persisted.difficultyFilter,
  themeFilter: persisted.themeFilter,
  dateFromFilter: persisted.dateFromFilter,
  dateToFilter: persisted.dateToFilter,
  usageFilter: persisted.usageFilter,

  // Actions
  setSearchQuery: (query: string) => {
    set({ searchQuery: query });
    persistFilters({ searchQuery: query });
  },
  setStatusFilter: (statuses: string[]) => {
    set({ statusFilter: statuses });
    persistFilters({ statusFilter: statuses });
  },
  setDifficultyFilter: (difficulties: string[]) => {
    set({ difficultyFilter: difficulties });
    persistFilters({ difficultyFilter: difficulties });
  },
  setThemeFilter: (themes: string[]) => {
    set({ themeFilter: themes });
    persistFilters({ themeFilter: themes });
  },
  setDateFromFilter: (date: string) => {
    set({ dateFromFilter: date });
    persistFilters({ dateFromFilter: date });
  },
  setDateToFilter: (date: string) => {
    set({ dateToFilter: date });
    persistFilters({ dateToFilter: date });
  },
  setUsageFilter: (filter: UsageFilter) => {
    set({ usageFilter: filter });
    persistFilters({ usageFilter: filter });
  },

  fetchWords: async () => {
    set({ loading: true, error: null });
    try {
      const data = await api.get('/api/words') as { words?: Record<string, unknown>[] };
      const words: Word[] = (data.words || []).map((w: Record<string, unknown>) => ({
        ...w,
        _stage: getWordStage(w),
      } as Word));
      set({ words, loading: false });
    } catch (err) {
      set({ error: (err as Error).message, loading: false });
    }
  },

  // H8 fixes (audit 2026-07-05): dirty flags never leaked across words before
  // these resets; rapid A→B switching let the slower fetch win; a failed
  // fetch left the previous word's body under the new selection.
  selectWord: async (wordId: string | null) => {
    useUiStore.getState().setNewWordMode(false);
    // Picking a word in the binder closes whatever full-page view is open
    // (Learning Layer, Knowledge, Word Bank, Test Bench, Ratings, Settings).
    // Without this the click selected the word behind a page that stayed up,
    // which read as the binder being dead. Every binder click funnels here.
    if (wordId) useUiStore.getState().setPage(null);
    if (!wordId) {
      set({ selectedWordId: null, selectedWord: null, wordVersion: null, dirtyFields: new Set(), error: null });
      return;
    }

    // Same-word refresh (bg-poll completion) keeps dirty flags — the caller
    // handles re-applying values. A genuine switch resets them.
    const isRefresh = get().selectedWordId === wordId;
    set({
      selectedWordId: wordId,
      error: null,
      ...(isRefresh ? {} : { dirtyFields: new Set<string>(), selectedWord: null, wordVersion: null }),
    });
    try {
      const data = await api.get(`/api/words/${wordId}`) as { word: Record<string, unknown> & { _version?: string } };
      // Out-of-order guard: if the user switched again while this fetch was
      // in flight, drop the stale response.
      if (get().selectedWordId !== wordId) return;
      const version = data.word._version ?? null;
      delete data.word._version;
      const word: Word = { ...data.word, _stage: getWordStage(data.word) } as Word;
      set({ selectedWord: word, wordVersion: version });
    } catch (err) {
      if (get().selectedWordId !== wordId) return;
      set({ error: (err as Error).message, selectedWord: null, wordVersion: null });
    }
  },

  setWordVersion: (version: string | null) => set({ wordVersion: version }),

  updateWordField: (field: string, value: unknown) => {
    set((s) => {
      if (!s.selectedWord) return s;
      const updated: Word = { ...s.selectedWord, [field]: value };
      updated._stage = getWordStage(updated);
      const dirty = new Set(s.dirtyFields);
      dirty.add(field);
      return { selectedWord: updated, dirtyFields: dirty };
    });
  },

  // Mirror a server-confirmed value into the selected word WITHOUT marking
  // it dirty. Use this whenever a generation / approval / sync flow returns
  // fields that the server has already accepted — those fields are not
  // "unsaved changes" the user needs to recover. updateWordField stays
  // reserved for genuine user edits that should surface in the recovery
  // banner if the user reloads before saving.
  setSelectedWordField: (field: string, value: unknown) => {
    set((s) => {
      if (!s.selectedWord) return s;
      const updated: Word = { ...s.selectedWord, [field]: value };
      updated._stage = getWordStage(updated);
      return { selectedWord: updated };
    });
  },

  clearDirty: () => set({ dirtyFields: new Set() }),

  clearDirtyFields: (fields: string[]) => {
    set((s) => {
      const dirty = new Set(s.dirtyFields);
      for (const f of fields) dirty.delete(f);
      return { dirtyFields: dirty };
    });
  },

  // Optimistically remove a word from the local list (e.g., after a successful
  // DELETE). Clears the selection if the deleted word was open.
  removeWord: (wordId: string) => {
    set((s) => {
      const words = s.words.filter((w) => w.id !== wordId);
      const wasSelected = s.selectedWordId === wordId || s.selectedWord?.id === wordId;
      return {
        words,
        selectedWordId: wasSelected ? null : s.selectedWordId,
        selectedWord: wasSelected ? null : s.selectedWord,
        wordVersion: wasSelected ? null : s.wordVersion,
      };
    });
  },

  // Get filtered and grouped words
  getFilteredWords: (): Word[] => {
    const {
      words, searchQuery, statusFilter, difficultyFilter,
      themeFilter, dateFromFilter, dateToFilter, usageFilter,
    } = get();
    let filtered = words;

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter((w) =>
        w.word?.toLowerCase().includes(q) ||
        w.definition_enhanced?.toLowerCase().includes(q) ||
        w.correct_definition?.toLowerCase().includes(q)
      );
    }

    if (usageFilter !== 'all') {
      filtered = filtered.filter((w) => {
        const hasContent = !!(w.definition_enhanced && String(w.definition_enhanced).trim());
        const isUsed = Boolean(w.game_date) || Boolean(w.transferred_to_d1);
        if (usageFilter === 'empty') return !hasContent;
        if (usageFilter === 'used') return hasContent && isUsed;
        if (usageFilter === 'unused') return hasContent && !isUsed;
        return true;
      });
    }

    if (statusFilter.length > 0) {
      filtered = filtered.filter((w) => statusFilter.includes(w._stage));
    }

    if (difficultyFilter.length > 0) {
      filtered = filtered.filter((w) => difficultyFilter.includes(w.difficulty as string));
    }

    if (themeFilter.length > 0) {
      filtered = filtered.filter((w) => {
        const themes = parseThemes(w.word_themes);
        return themes.some((t) => themeFilter.includes(t));
      });
    }

    if (dateFromFilter || dateToFilter) {
      filtered = filtered.filter((w) => {
        if (!w.game_date) return false;
        if (dateFromFilter && w.game_date < dateFromFilter) return false;
        if (dateToFilter && w.game_date > dateToFilter) return false;
        return true;
      });
    }

    return filtered;
  },

  getAllThemes: (): string[] => {
    const { words } = get();
    const set = new Set<string>();
    for (const w of words) {
      for (const t of parseThemes(w.word_themes)) set.add(t);
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  },

  getGroupedWords: (): Record<string, Word[]> => {
    const filtered = get().getFilteredWords();
    const groups: Record<string, Word[]> = {};

    for (const stage of PIPELINE_STAGES) {
      const stageWords = filtered.filter((w) => w._stage === stage.id);
      if (stageWords.length > 0) {
        groups[stage.id] = stageWords;
      }
    }

    return groups;
  },
}));
